import { useCallback, useState } from 'react';
import {
  CalendarX, ClipboardCheck, Truck, ShieldCheck, Clock, CheckCircle2,
  RefreshCw, ScanLine, ArrowDownUp, ArrowDownToLine, ArrowUpFromLine,
  ArrowLeftRight, ShoppingCart, PlusCircle, LifeBuoy,
} from 'lucide-react';
import { useAuth } from '../lib/contextoAuth';
import { useDatosDeTrabajo } from '../lib/useDatosDeTrabajo';
import Ayuda from '../ui/Ayuda';
import AvisoCopiaLocal from '../ui/AvisoCopiaLocal';
import Reportar from '../tickets/Reportar';

// Lo que me toca hoy.
//
// EL PROBLEMA QUE CIERRA. El sistema registra muy bien lo que YA se hizo y
// no dice en ningún sitio QUÉ HAY QUE HACER. Quien opera el almacén entra,
// escanea, y cada movimiento es un viaje de ida y vuelta: no existe «lo
// pendiente de hoy» ni una lista que seguir. La diferencia entre un sistema
// que anota y uno que dirige es exactamente esta pantalla.
//
// No inventa nada: todo sale de datos que ya estaban. Lo que faltaba era
// juntarlos, ordenarlos por lo que estorba a más gente, y —esto es lo que
// hace que se use— que cada tarjeta LLEVE a donde se resuelve.

const ICONO = {
  CADUCADO: CalendarX,
  CONTEO: ClipboardCheck,
  RECEPCION: Truck,
  REPOSICION: ArrowDownUp,
  APROBACION: ShieldCheck,
  ESPERANDO: Clock,
};

// Rojo solo para lo que empeora si se deja. Si todo fuera rojo, el rojo no
// significaría nada — la misma regla que en el panel de tareas programadas.
const TONO = {
  ALTA: 'border-red-200 bg-red-50 hover:bg-red-100',
  MEDIA: 'border-amber-200 bg-amber-50 hover:bg-amber-100',
  BAJA: 'border-gray-200 bg-white hover:bg-gray-50',
};
const TONO_ICONO = {
  ALTA: 'text-red-700', MEDIA: 'text-amber-700', BAJA: 'text-gray-500',
};

// `visibles` (P17): los ids de los módulos que esta persona puede abrir; lo
// pasa AppShell. Sin él no se pinta ningún atajo que dependa de permisos.
export default function MiTrabajo({ irA, visibles }) {
  const { usuario, tieneNivel } = useAuth();
  // DIRECTRIZ-PWA F2 (11 oct 2026, DISENO-F2 §4.4; parche del integrador
  // para experiencia): la lista pasa por `useDatosDeTrabajo`, que guarda la
  // última respuesta buena y, sin señal, la enseña CON su hora (`momento`).
  // `tareas === null` sigue siendo «cargando»; `[]` sigue siendo el vacío.
  const { datos: tareas, error, momento, recargar } = useDatosDeTrabajo('/mi-trabajo');

  // CUÁNDO SE ACTUALIZÓ, para que «Actualizar» confirme visiblemente que
  // ocurrió aunque la lista salga igual (Doctrina 2, regla 7; auditoría de
  // botones del 19 sep 2026: la pulsación pedía al servidor y no cambiaba
  // nada a la vista).
  const [actualizadoA, setActualizadoA] = useState(null);
  const cargar = useCallback(
    () => Promise.resolve(recargar()).then(() => setActualizadoA(new Date())),
    [recargar],
  );

  const nombre = (usuario?.nombre || '').split(' ')[0];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {nombre ? `Hola, ${nombre}` : 'Mi trabajo'}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Lo que está pendiente ahora mismo, empezando por lo que más estorba.
          </p>
        </div>
        <button
          type="button" onClick={cargar}
          className="inline-flex items-center gap-1.5 border border-gray-300 bg-white
                     text-gray-700 text-sm font-semibold px-3 py-2 rounded-lg
                     hover:bg-gray-50 focus:outline-none focus:ring-2
                     focus:ring-[var(--color-marca)] focus:ring-offset-1"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" /> Actualizar
        </button>
      </div>
      <AccesosDelTelefono irA={irA} visibles={visibles}
                          puedeMover={Boolean(tieneNivel?.('OPERADOR'))} />

      <AvisoCopiaLocal momento={momento} que="Tus pendientes" />

      {actualizadoA && momento && (
        <p role="status" className="text-xs text-gray-500 -mt-2">
          Sin señal: se enseña la copia de las {momento.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}.
        </p>
      )}
      {actualizadoA && !momento && (
        <p role="status" className="text-xs text-gray-500 -mt-2">
          Actualizado a las {actualizadoA.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}.
        </p>
      )}

      {error && (
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          {error}
        </div>
      )}

      {tareas === null && !error && (
        <p role="status" className="text-sm text-gray-500">Mirando qué hay pendiente…</p>
      )}

      {/* CERO PENDIENTES ES UNA RESPUESTA, no una pantalla rota. Y se dice
          qué aparecería aquí, para que el vacío sea creíble en vez de
          parecer que algo no cargó. */}
      {tareas?.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
          <CheckCircle2 className="h-10 w-10 text-green-700 mx-auto mb-2" aria-hidden="true" />
          <p className="font-semibold text-gray-900">No tienes nada pendiente</p>
          <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
            Aquí aparecen las hojas de conteo sin terminar, la mercancía que se
            está esperando, los lotes vencidos que hay que retirar y las
            autorizaciones atascadas, y los huecos de surtido que hay que
            reponer. Ahora mismo no hay ninguna.
          </p>
        </div>
      )}

      {tareas?.length > 0 && (
        <ul className="space-y-2">
          {tareas.map((t) => {
            const Icono = ICONO[t.tipo] || ClipboardCheck;
            return (
              <li key={`${t.tipo}:${t.asignada}`}>
                {/* Toda la tarjeta es el botón: con guantes, un enlace de
                    dos palabras no se acierta. */}
                <button
                  type="button"
                  onClick={() => irA(t.modulo, t.contexto || {})}
                  className={`w-full text-left border rounded-xl p-4 flex items-start gap-3
                              transition-colors focus:outline-none focus:ring-2
                              focus:ring-[var(--color-marca)] focus:ring-offset-1 ${
                    TONO[t.urgencia] || TONO.BAJA}`}
                >
                  <Icono className={`h-6 w-6 shrink-0 mt-0.5 ${
                    TONO_ICONO[t.urgencia] || TONO_ICONO.BAJA}`} aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">
                        {t.titulo}
                      </span>
                      {/* «Tuyo» y «libre» son dos cosas distintas y tienen
                          que verse como tales: lo primero es un
                          compromiso, lo segundo una oferta. Sin la
                          etiqueta, dos tarjetas parecidas se leen como una
                          repetida y se ignora una de las dos. */}
                      {t.asignada === true && (
                        <span className="text-[11px] font-bold uppercase tracking-wide
                                         text-[var(--color-marca)] bg-[var(--color-marca-suave)] px-1.5 py-0.5 rounded">
                          tuyo
                        </span>
                      )}
                      {t.asignada === false && (
                        <span className="text-[11px] font-semibold uppercase tracking-wide
                                         text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                          sin asignar
                        </span>
                      )}
                    </span>
                    <span className="block text-sm text-gray-600 leading-snug mt-0.5">
                      {t.detalle}
                    </span>
                  </span>
                  <span className="text-2xl font-bold tabular-nums text-gray-900 shrink-0">
                    {t.cantidad}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* El escáner queda a un toque: es lo que más se usa, y esta pantalla
          no puede convertirse en un desvío para llegar a él. */}
      <button
        type="button"
        onClick={() => irA('escanear', {})}
        className="w-full flex items-center justify-center gap-2 bg-[var(--color-marca)]
                   hover:bg-[var(--color-marca-fuerte)] text-white font-semibold py-3 rounded-xl
                   focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]
                   focus:ring-offset-2"
      >
        <ScanLine className="h-5 w-5" aria-hidden="true" /> Escanear algo
      </button>
    </div>
  );
}

// =====================================================================
// A UN TOQUE · PLAN-DE-CIERRE-PILOTO P17 (29 sep 2026)
// =====================================================================
// Medido en un Chromium real a 390 px (LISTO-PARA-PILOTO j, hallazgo 1):
// en el teléfono TODA tarea empezaba con un toque de más —abrir el menú—,
// y «Punto de venta» y «Alta rápida» vivían dentro de «Más opciones»: seis
// toques la primera vez. Aquí quedan las tareas del día a un toque.
//
// SOLO EN EL TELÉFONO (`md:hidden`): en pantalla ancha el menú está siempre
// a la vista y esto sería repetirlo.
//
// CADA ATAJO RESPETA LOS PERMISOS. Sale solo si el módulo al que lleva está
// entre los `visibles` de esta persona (la misma regla del menú,
// `modulosVisibles`), y Entrada/Salida/Traspaso además piden poder mover
// (OPERADOR), igual que el formulario del escáner. «Reportar un problema»
// sale siempre: todos pueden pedir ayuda (`tickets.reportar`, de fábrica),
// y abre el mismo formulario que el flotante «Pedir ayuda».
function AccesosDelTelefono({ irA, visibles, puedeMover }) {
  const [reportando, setReportando] = useState(false);
  const ve = (id) => Array.isArray(visibles) && visibles.includes(id);
  const atajos = [
    ve('escanear') && { id: 'escanear', etiqueta: 'Escanear', icono: ScanLine, ir: () => irA('escanear', {}), principal: true },
    ve('escanear') && puedeMover && { id: 'entrada', etiqueta: 'Entrada', icono: ArrowDownToLine, ir: () => irA('escanear', { tipo: 'ENTRADA' }) },
    ve('escanear') && puedeMover && { id: 'salida', etiqueta: 'Salida', icono: ArrowUpFromLine, ir: () => irA('escanear', { tipo: 'SALIDA' }) },
    ve('escanear') && puedeMover && { id: 'traspaso', etiqueta: 'Traspaso', icono: ArrowLeftRight, ir: () => irA('escanear', { tipo: 'TRASPASO' }) },
    ve('pos') && { id: 'pos', etiqueta: 'Punto de venta', icono: ShoppingCart, ir: () => irA('pos', {}) },
    ve('alta') && { id: 'alta', etiqueta: 'Alta rápida', icono: PlusCircle, ir: () => irA('alta', {}) },
    { id: 'reportar', etiqueta: 'Reportar un problema', icono: LifeBuoy, ir: () => setReportando(true) },
  ].filter(Boolean);
  const mueve = atajos.some((a) => a.id === 'entrada');

  return (
    <section aria-label="A un toque" className="md:hidden">
      <h3 className="text-xs font-bold uppercase tracking-wide text-gray-600 mb-2">A un toque</h3>
      <div className="grid grid-cols-3 gap-2">
        {atajos.map((a) => {
          const Icono = a.icono;
          return (
            <button
              key={a.id}
              type="button"
              onClick={a.ir}
              className={`min-h-[76px] flex flex-col items-center justify-center gap-1 rounded-xl border
                          px-1 py-2 text-sm font-semibold leading-tight text-center
                          focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)] focus:ring-offset-1 ${
                a.principal
                  ? 'bg-[var(--color-marca)] border-[var(--color-marca)] text-white'
                  : 'bg-white border-gray-200 text-gray-800 hover:bg-gray-50'}`}
            >
              <Icono className="h-6 w-6 shrink-0" aria-hidden="true" />
              {a.etiqueta}
            </button>
          );
        })}
      </div>
      {mueve && <Ayuda id="mi_trabajo.accesos" />}
      {reportando && <Reportar onCerrar={() => setReportando(false)} onIr={irA} />}
    </section>
  );
}
